/**
 * Fetch Blog pages by tags using contentfulMetadata filter
 * Queries pageCollection and extracts Blog data from mainRegionCollection
 *
 * Usage:
 * - First render: pass all filterTags to show all blog pages with any of those tags
 * - After selection: pass selected tags only to filter by specific tags
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {pageWhere} from '@amblique/king-living/app/contentful/helpers/region';
import {getBlogListingPages} from '@amblique/king-living/app/contentful/queries/blog-listing-pages.queries';
import {
  PAGE_BLOG_METADATA_TAG_IDS,
  categoryTagIdsFromTerms,
} from '@amblique/king-living/app/contentful/utils/blog-metadata-tag-ids';

/**
 * Default page size for blog listing pagination
 */
export const PAGE_SIZE = 12;

/**
 * Fetch Blog pages by category tags using contentfulMetadata filter
 *
 * @param {string[]} categoryTags - Display names (BlogListing) **or** Contentful metadata tag ids when `useRawArticleTagIds` is true
 * @param {Object} [options={}] - Hook options
 * @param {number} [options.limit=PAGE_SIZE] - Maximum number of items to fetch per page (defaults to PAGE_SIZE constant)
 * @param {number} [options.skip=0] - Number of items to skip (for pagination)
 * @param {boolean} [options.useRawArticleTagIds=false] - When true, marks Search idle article-tag queries in the React Query cache key
 * @param {import('@tanstack/react-query').UseQueryOptions} [options.queryOptions={}] - React Query options
 * @returns {Object} Query result with pages and blogs
 * @returns {Object} return.data - PageCollection with items containing Blog data in mainRegionCollection
 * @returns {number} return.data.total - Total count of matching pages
 * @returns {number} return.data.skip - Number of items skipped
 * @returns {number} return.data.limit - Number of items per page
 * @returns {Array} return.data.items - Pages with Blog data
 * @returns {boolean} return.isLoading - Loading state
 * @returns {boolean} return.isError - Error state
 * @returns {Error} return.error - Error object if any
 */
export function useBlogsPagesByTags(categoryTags = [], options = {}) {
  const contentfulService = useService(ContentfulService);
  const {site} = useMultiSite();

  const normalizedTags = categoryTags.map((t) => String(t).trim()).filter(Boolean);
  const useRawArticleTagIds = Boolean(options.useRawArticleTagIds);

  const tagIds = [...new Set(normalizedTags.flatMap((t) => categoryTagIdsFromTerms([t])))];

  const blogPageTagClause = {
    contentfulMetadata: {
      tags: {
        id_contains_some: PAGE_BLOG_METADATA_TAG_IDS,
      },
    },
  };

  const filterClauses = [blogPageTagClause];

  if (normalizedTags.length > 0) {
    filterClauses.push({
      contentfulMetadata: {
        tags: {
          id_contains_some: tagIds,
        },
      },
    });
  }

  const where = pageWhere(filterClauses, contentfulService?.locale, site?.id);

  const limit = options.limit || PAGE_SIZE;
  const skip = options.skip || 0;

  const {queryOptions = {}} = options;
  const {enabled: enabledFromOptions, ...restQueryOptions} = queryOptions;
  const resolvedEnabled =
    !!contentfulService &&
    (typeof enabledFromOptions === 'boolean' ? enabledFromOptions : true);

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'blog-listing-pages',
      contentfulService?.locale,
      site?.id,
      JSON.stringify(normalizedTags),
      useRawArticleTagIds ? 'raw' : 'display',
      limit,
      skip
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(getBlogListingPages),
        {
          limit,
          skip,
          locale: contentfulService?.locale,
          preview: false,
          where,
          order: ['sys_publishedAt_DESC'],
        }
      );

      return data.pageCollection || {total: 0, skip: 0, limit: 0, items: []};
    },

    enabled: resolvedEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...restQueryOptions,
  });
}
